import { useEffect, useState } from 'react';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { toast } from 'react-toastify';

interface AlarmEvent {
  plantName: string;
  controlValue: boolean;
  error: string;
  message: string;
}

export const usePlantAlarmSSE = (
  enabled: boolean,
  dataLoggerId: string,
  selectedPlant: any,
  bidirectionalLogId: string
) => {
  const [events, setEvents] = useState<AlarmEvent[]>([]);
  const [isWaiting, setIsWaiting] = useState(false);
  const [isIncomingData, setIsIncomingData] = useState<any>(true);

  const backendApi = import.meta.env.VITE_APP_ENDPOINT;
  const token = localStorage.getItem('token');

  const resetEvents = () => {
    setEvents([]);
    setIsWaiting(false);
    setIsIncomingData(false);
  };

  useEffect(() => {
    if (!enabled || !token || !dataLoggerId || !bidirectionalLogId || !selectedPlant) {
      setEvents([]);
      setIsWaiting(false);
      setIsIncomingData(false);
      return;
    }
    setIsIncomingData(true);
    setIsWaiting(true);

    const eventSource = new EventSourcePolyfill(
      `${backendApi}/biDirectionalControl/getBidirectionalStatus?dataLoggerId=${dataLoggerId}&bidirectionalLogId=${bidirectionalLogId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        heartbeatTimeout: 9000000,
        withCredentials: false,
      }
    );

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data) as AlarmEvent;

        if (data?.error) {
          toast.error(data.error);
          setEvents([{ ...data }]);
          eventSource.close();
          setIsWaiting(false);
          setIsIncomingData(false);
          return;
        }

        if (data?.message) {
          setIsIncomingData(false);
          eventSource.close();
          setIsWaiting(false);
          return;
        }

        setIsWaiting(false);

        if (data.controlValue === false) {
          eventSource.close();
          return;
        }

        setEvents((prev) => {
          const updated = [...prev];
          const index = updated.findIndex((e) => e.plantName === data.plantName);
          if (index !== -1) {
            updated[index] = data;
          } else {
            updated.push(data);
          }
          return updated;
        });
      } catch (err) {
        console.error('Failed to parse SSE data', err);
      }
    };

    eventSource.onerror = (err) => {
      console.error('SSE error:', err);
      eventSource.close();
      setIsWaiting(false);
    };

    return () => {
      eventSource.close();
      setIsWaiting(false);
      setIsIncomingData(false);
    };
  }, [enabled, backendApi, token, dataLoggerId, selectedPlant, bidirectionalLogId]);

  return { events, isWaiting, resetEvents, isIncomingData };
};
